import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Popup } from 'semantic-ui-react';
import { useLocalStorage } from 'usehooks-ts';

import { useIsDarkMode } from './dark-mode-provider';
import s from './dark-mode-toggle.scss';

// How long the confirmation tooltip stays visible after a toggle.
const tooltipDurationMs = 2000;

/**
 * A switch to enable or disable dark mode, persisted to local storage so the
 * preference is restored on subsequent visits (an unset value follows the
 * system preference).
 *
 * @see DarkModeProvider
 */
export const DarkModeToggle = () => {
  useStyles(s);

  const isDarkMode = useIsDarkMode();

  // Persist the preference; the resulting state is read back via context.
  const [, setStoredPreference] = useLocalStorage('darkmode', false);

  // The tooltip is shown only briefly after a toggle, never on hover or focus.
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(tooltipTimeout.current), []);

  const toggleDarkMode = () => {
    setStoredPreference(!isDarkMode);

    setShowTooltip(true);
    clearTimeout(tooltipTimeout.current);
    tooltipTimeout.current = setTimeout(
      () => setShowTooltip(false),
      tooltipDurationMs,
    );
  };

  return (
    <div className={s.root}>
      <Popup
        className={s.tooltip}
        content={
          isDarkMode
            ? 'Hello darkness my old friend...'
            : "Here comes the sun, and I say, it's alright..."
        }
        on={[]}
        open={showTooltip}
        position="left center"
        size="mini"
        trigger={
          <button
            type="button"
            role="switch"
            aria-checked={isDarkMode}
            aria-label={
              isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
            }
            className={classNames(s.switch, { [s.checked]: isDarkMode })}
            onClick={() => toggleDarkMode()}
          >
            <span className={s.knob}>
              {isDarkMode ? <Moon aria-hidden /> : <Sun aria-hidden />}
            </span>
          </button>
        }
      />
    </div>
  );
};

export default DarkModeToggle;
